import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useHasPermission } from '@/auth/hooks'
import { useStore } from '@/store'
import { FleetLoadSection } from './FleetLoadSection'
import TickTimestampFilter from '@/components/filters/TickTimestampFilter'

type PanelTab = 'agents' | 'tick'

const TAB_HELP: Record<PanelTab, string> = {
  agents: 'Controller + agents → public /ingest (preferred load path).',
  tick: 'Paste an AcePlayer tick timestamp to jump the historic 10s agg window (dev compare).',
}

const AMP_Z = 20000

export function AmplifyPanel({
  open,
  onClose,
  anchorRef,
}: {
  open: boolean
  onClose: () => void
  anchorRef: React.RefObject<HTMLElement | null>
}) {
  const canFleet = useHasPermission('fleet:control')
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; right: number }>({ top: 52, right: 12 })
  const [tab, setTab] = useState<PanelTab>(canFleet ? 'agents' : 'tick')

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const el = anchorRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setPos({
        top: Math.round(r.bottom + 8),
        right: Math.max(12, Math.round(window.innerWidth - r.right)),
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, anchorRef])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node | null
      if (!t) return
      if (panelRef.current?.contains(t)) return
      if (anchorRef.current?.contains(t)) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [open, onClose, anchorRef])

  if (!open) return null

  const tabs: { id: PanelTab; label: string; show: boolean }[] = [
    { id: 'agents', label: 'Agents', show: canFleet },
    { id: 'tick', label: 'Tick ts', show: true },
  ]
  const visibleTabs = tabs.filter((t) => t.show)
  const activeTab = visibleTabs.some((t) => t.id === tab) ? tab : (visibleTabs[0]?.id ?? 'tick')

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Dev tools / Load"
      style={{
        position: 'fixed',
        top: pos.top,
        right: pos.right,
        width: 440,
        maxHeight: 'min(78vh, 720px)',
        overflowY: 'auto',
        zIndex: AMP_Z,
        background: 'var(--bg-1)',
        border: '0.5px solid var(--bd-0)',
        borderRadius: 10,
        boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
        padding: 14,
        color: 'var(--t-0)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <strong style={{ fontSize: 13 }}>Dev tools</strong>
        <span
          style={{
            fontSize: 10,
            padding: '2px 6px',
            borderRadius: 4,
            background: 'rgba(38,130,24,0.2)',
            color: 'var(--t-1)',
          }}
        >
          ready
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onClose}
          style={{ border: 'none', background: 'transparent', color: 'var(--t-2)', cursor: 'pointer' }}
        >
          ✕
        </button>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              flex: 1,
              fontSize: 10,
              fontWeight: 600,
              padding: '6px 4px',
              borderRadius: 6,
              border: `0.5px solid ${activeTab === t.id ? 'var(--info-bd)' : 'var(--bd-0)'}`,
              background: activeTab === t.id ? 'var(--info-bg)' : 'transparent',
              color: activeTab === t.id ? 'var(--info)' : 'var(--t-1)',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <p style={{ fontSize: 10, color: 'var(--t-2)', marginTop: 0, marginBottom: 10 }}>
        {TAB_HELP[activeTab]}
      </p>

      {activeTab === 'tick' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--t-2)', lineHeight: 1.4 }}>
            Paste an AcePlayer tick timestamp to set a historic 10s aggregation window for E2E compare.
          </div>
          <TickTimestampFilter />
        </div>
      )}

      {activeTab === 'agents' && canFleet && <FleetLoadSection compact />}

      {activeTab === 'agents' && !canFleet && (
        <p style={{ fontSize: 11, color: 'var(--crit)' }}>
          Requires fleet:control permission to drive agents.
        </p>
      )}
    </div>,
    document.body,
  )
}

/** Topbar chip — Dev tools (agents + AcePlayer tick). */
export function AmplifyChip() {
  const chipRef = useRef<HTMLButtonElement | null>(null)
  const overlayPanel = useStore((s) => s.overlayPanel)
  const toggleOverlayPanel = useStore((s) => s.toggleOverlayPanel)
  const setOverlayPanel = useStore((s) => s.setOverlayPanel)
  const open = overlayPanel === 'amplify'

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={chipRef}
        type="button"
        onClick={() => toggleOverlayPanel('amplify')}
        aria-label="Dev tools / Load"
        aria-expanded={open}
        style={{
          fontSize: 11,
          fontWeight: 600,
          padding: '4px 8px',
          borderRadius: 6,
          border: '0.5px solid var(--bd-0)',
          background: 'transparent',
          color: 'var(--t-0)',
          cursor: 'pointer',
        }}
      >
        Dev tools
      </button>
      <AmplifyPanel open={open} onClose={() => setOverlayPanel(null)} anchorRef={chipRef} />
    </div>
  )
}
